import {
  Fragment,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Input,
  Text,
  WidgetCard,
  useIsMobile,
} from "@salestrak/ui";
import { SUMMARY_SCROLL_END_GUTTER } from "#app/widgets/summary/summary-widget-layout";
import { SummaryEmptyFoldProvider } from "#app/components/issue-view/summary-fold-row";
import {
  DETAIL_EDIT,
  useIssueViewMutations,
} from "#app/hooks/issue-view/use-issue-view-mutations";
import { useMigrateIssueType } from "#app/hooks/issue-view/use-migrate-issue-type";
import { useCustomFieldValueCommit } from "#app/hooks/issue-view/use-custom-field-value-commit";
import { useIssueViewCapabilities } from "./issue-view-capabilities";
import type {
  IssueViewStage,
  IssueTypeWithFields,
  CustomFieldDef,
} from "./issue-view-types";
import type { EntityRefValue, IssueRow } from "#app/lib/data";
import { MigrateIssueTypeDialog } from "./migrate-issue-type-dialog";
import { IssueTypeSelector } from "#app/components/entity-selectors";
import { typeInHero } from "#app/lib/type-placement";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { useCollapsedSummarySections } from "#app/lib/hooks/use-collapsed-summary-sections";
import {
  CREATE_MODE_WIDGET_TYPES,
  ISSUE_PROPERTIES_WIDGET,
  ISSUE_SORTABLE_WIDGETS,
  resolveTypeSectionLayout,
} from "#app/widgets/issue/issue-summary-stack";
import { resultMessage, toastMutationError } from "#app/lib/utils/toast-errors";
import { pageLeaveContext } from "#app/lib/page-leave";
// Direct module path, NEVER the feature barrel: the barrel side-effect-registers
// the issue defs into `#app/widgets/registry`, and the registry's own evaluation
// reaches this file (team-members def → issue-view barrel) — a barrel import
// here would re-enter the half-evaluated registry module and crash.
import {
  IssueWidgetProvider,
  type IssueWidgetNavigateTab,
} from "#app/widgets/issue/issue-widget-context";
import {
  SummaryWidgetStack,
  SummarySectionDivider,
} from "#app/widgets/summary/summary-widget-stack";
import type { ActivityContextValue } from "./activity/activity-context";
import type { NoteNode } from "./activity/activity-types";
import type { IssueMediaItem } from "#app/hooks/issue-view/use-issue-media-items";
import type { Omittable } from "@salestrak/ui";
import { TITLE_INPUT } from "#app/components/detail-view/title-input";

type IssueViewSummaryProps = Omittable<{
  issue?: IssueRow;
  stage?: IssueViewStage | null;
  workflowId?: string | null;
  address?: {
    id: string;
    addressId: string;
    wholeAddress?: string | null;
    latLng?: { lat: number; lng: number } | null;
  } | null;
  assignees?: EntityRefValue[];
  issueTypes?: IssueTypeWithFields[];
  values?: IssueRow["values"];
  customFields?: CustomFieldDef[];
  onCloseIssue?: () => void;
  contentMaxWidth?: number;
  /** Scroll to + highlight this custom field row on mount. */
  focusCustomFieldId?: string | null;
  /** Focus the title input — a fresh nonce per request (the menu Rename
   *  fallback for hosts with no inline rename surface). */
  focusTitleNonce?: number;
  // Widget-stack inputs: the comments/attachments widgets read the activity
  // feed, notes, and merged media items the issue body already resolves;
  // `onNavigateTab` deep-links out of a widget to another tab.
  onNavigateTab?: (tab: IssueWidgetNavigateTab) => void;
  activity?: ActivityContextValue;
  activityLoading?: boolean;
  notes?: NoteNode[];
  mediaItems?: IssueMediaItem[];
}>;

// ---------------------------------------------------------------------------
// Widget stack: properties pinned, then the sections the project's TYPE
// orders (and, for Description, shows or hides) for everyone — see
// `#app/widgets/issue/issue-summary-stack`. The viewer's folds are theirs
// (`useCollapsedSummarySections`); nothing else is remembered.
// ---------------------------------------------------------------------------

// The omnibus `updateIssue` here only renames.
const SUMMARY_EDIT = { ...DETAIL_EDIT, updateIssueLabel: "rename" };

// The localStorage stack entries nothing reads any more (the type owns the
// layout now), removed once per load.
const RETIRED_ISSUE_WIDGET_KEYS = [
  "issue-widgets-order",
  "issue-widgets-hidden",
  "issue-widgets-collapsed",
  "issue-widgets-v2-order",
  "issue-widgets-v2-hidden",
  "issue-widgets-v2-collapsed",
];
let retiredKeysCleared = false;
function clearRetiredIssueWidgetKeys() {
  if (retiredKeysCleared) return;
  retiredKeysCleared = true;
  try {
    for (const key of RETIRED_ISSUE_WIDGET_KEYS) window.localStorage.removeItem(key);
  } catch {
    // Storage blocked (private mode): nothing to clear.
  }
}

export function IssueViewSummary({
  issue,
  stage,
  workflowId,
  address,
  assignees,
  issueTypes,
  values,
  customFields,
  onCloseIssue,
  contentMaxWidth,
  focusCustomFieldId,
  focusTitleNonce,
  onNavigateTab,
  activity,
  activityLoading,
  notes,
  mediaItems,
}: IssueViewSummaryProps) {
  const createCtx = useCreateIssueMode();
  const isCreateMode = !!createCtx;
  useEffect(clearRetiredIssueWidgetKeys, []);
  const isMobile = useIsMobile();
  // The viewer's folds for the create-mode cards — the same account
  // preference the detail stack reads, so the two surfaces cannot disagree.
  const { collapsedSet, setCollapsed } = useCollapsedSummarySections();

  // ---------------------------------------------------------------------------
  // Issue type selection
  // ---------------------------------------------------------------------------

  // The omnibus `updateIssue` drives name renames; type migration goes through
  // the shared `useMigrateIssueType`; custom-field values commit through the
  // shared `useCustomFieldValueCommit` path; photos-field tiles ride the
  // add/remove delta mutations.
  const {
    updateIssue,
    updateDescription,
    addPhotoFieldValue,
    removePhotoFieldValue,
  } = useIssueViewMutations(SUMMARY_EDIT);
  const migrateIssueType = useMigrateIssueType();
  const commitCustomFieldValue = useCustomFieldValueCommit({ tracking: DETAIL_EDIT });
  // View / comment access → no name edits, no custom field writes,
  // no issue-type migration. Create mode bypasses (no issue yet).
  const { canEdit } = useIssueViewCapabilities();
  const editableHere = isCreateMode || canEdit;

  // ---------------------------------------------------------------------------
  // Issue type migration
  // ---------------------------------------------------------------------------

  const [migrationTarget, setMigrationTarget] = useState<string | null>(null);

  const handleIssueTypeChange = useCallback(
    (issueTypeId: string) => {
      if (isCreateMode) {
        createCtx.updateDraft({ issueTypeId });
        return;
      }
      if (issueTypeId === issue?.issueTypeId) return;
      setMigrationTarget(issueTypeId);
    },
    [isCreateMode, createCtx, issue?.issueTypeId],
  );

  const handleMigrateConfirm = useCallback(
    async (issueTypeId: string, migrateValues: Record<string, unknown>) => {
      if (!issue) return;
      await migrateIssueType(issue.issueId, issueTypeId, migrateValues, "detail");
      setMigrationTarget(null);
    },
    [issue, migrateIssueType],
  );

  // ---------------------------------------------------------------------------
  // Name update (view mode: mutation on blur; create mode: draft update)
  // ---------------------------------------------------------------------------

  const savedNameRef = useRef(issue?.name ?? "");
  const draftNameRef = useRef(issue?.name ?? "");
  const nameInputRef = useRef<HTMLInputElement>(null);

  // In create mode, focus the title once the container (sheet) signals it's
  // safe to do so. On mobile, focusing during the sheet's open animation
  // opens the virtual keyboard and desyncs the sheet's translateY math. On
  // desktop, `canAutoFocus` defaults to true so this fires on mount.
  const canAutoFocus = createCtx?.canAutoFocus ?? false;
  useEffect(() => {
    if (isCreateMode && canAutoFocus) {
      nameInputRef.current?.focus();
    }
  }, [isCreateMode, canAutoFocus]);

  // The menu Rename fallback: a host with no inline rename surface opens the
  // project with `focusTitle`, which lands here as a fresh nonce per request
  // — so a repeat Rename on the already-open issue re-focuses. The rAF lets
  // the dialog's own open-focus management land first, or it would steal the
  // title focus right back.
  useEffect(() => {
    if (!focusTitleNonce) return;
    const raf = requestAnimationFrame(() => nameInputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [focusTitleNonce]);

  // Keyed on the ISSUE, not on the issue object: this seeds the name refs when
  // a different issue opens. Depending on `issue` would re-seed them whenever
  // the server echoes a name back, wiping whatever the user is typing.
  useEffect(() => {
    if (!issue) return;
    savedNameRef.current = issue.name;
    draftNameRef.current = issue.name;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the issue id, not the object
  }, [issue?.issueId]);

  // A rename from another session or an automation reaches `issue.name`
  // through the cache, but the input is uncontrolled and shows only the name
  // it mounted with. Copy each new name into it, except over unsaved typing:
  // while the user edits a different name, keep their text, and their blur
  // saves it over the remote name. A blurred input has nothing unsaved.
  const serverName = issue?.name;
  useEffect(() => {
    if (isCreateMode || serverName === undefined) return;
    const input = nameInputRef.current;
    const editing =
      input !== null &&
      input === document.activeElement &&
      draftNameRef.current.trim() !== savedNameRef.current;
    savedNameRef.current = serverName;
    if (editing) return;
    draftNameRef.current = serverName;
    if (input && input.value !== serverName) input.value = serverName;
  }, [serverName, isCreateMode]);

  const handleNameChange = useCallback(
    (text: string) => {
      draftNameRef.current = text;
      if (isCreateMode) {
        createCtx.updateDraft({ name: text });
      }
    },
    [isCreateMode, createCtx],
  );

  const titleMissing =
    isCreateMode && createCtx.submitted && !createCtx.draft.name.trim();

  // The name a rename in flight is saving, so a second blur with the same
  // text does not send it twice.
  const pendingNameRef = useRef<string | null>(null);

  // Puts the saved name back into the input after a blank title is left:
  // the input must not show a name the server does not hold.
  const restoreSavedName = useCallback(() => {
    draftNameRef.current = savedNameRef.current;
    const input = nameInputRef.current;
    if (input) input.value = savedNameRef.current;
  }, []);

  const handleNameBlur = useCallback(() => {
    if (isCreateMode || !issue) return;
    if (!editableHere) return;
    const trimmed = draftNameRef.current.trim();
    // A project needs a title: a cleared one is refused, and the saved
    // name comes back.
    if (!trimmed) {
      restoreSavedName();
      return;
    }
    if (trimmed === savedNameRef.current || trimmed === pendingNameRef.current) return;
    pendingNameRef.current = trimmed;
    // Use the omnibus UpdateIssue mutation — server returns the
    // full IssueViewIssue fragment, Apollo merges by id. Skip the
    // optimistic response since a blur-driven rename doesn't need
    // sub-roundtrip latency masking, and crafting a full
    // IssueViewIssue payload here would be brittle. The saved name moves
    // only when the server answers with the Issue, so a failed save keeps
    // the typed text and the next blur or Enter tries again.
    void updateIssue({
      variables: { issueId: issue.issueId, input: { name: trimmed } },
    })
      .then((result) => {
        const payload = result.data?.updateIssue;
        if (payload?.__typename !== "Issue") {
          throw new Error(resultMessage(payload) ?? "Title not saved");
        }
        savedNameRef.current = trimmed;
      })
      .catch((err: unknown) => {
        toastMutationError(err, "Failed to rename project");
      })
      .finally(() => {
        if (pendingNameRef.current === trimmed) pendingNameRef.current = null;
      });
  }, [isCreateMode, editableHere, updateIssue, issue, restoreSavedName]);

  // Enter saves the title the way a click away does, and leaves the field.
  const handleNameKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isCreateMode || event.nativeEvent.isComposing) return;
      if (event.key === "Enter") {
        event.preventDefault();
        event.currentTarget.blur();
      }
    },
    [isCreateMode],
  );

  // ---------------------------------------------------------------------------
  // Custom field value updates
  // ---------------------------------------------------------------------------

  // Create mode writes into the draft; view mode commits through the shared
  // single-field write path (`undefined` is the row's clear signal — a clear
  // deletes the row rather than storing an empty value).
  const handleCustomFieldUpdate = useCallback(
    (data: { customFieldId: string; value: unknown }) => {
      if (isCreateMode) {
        createCtx.updateCustomFieldValue(data.customFieldId, data.value);
        return;
      }
      if (!issue || !editableHere) return;
      commitCustomFieldValue(
        { issueId: issue.issueId, values: values ?? null },
        data.customFieldId,
        data.value,
      );
    },
    [isCreateMode, createCtx, issue, editableHere, values, commitCustomFieldValue],
  );

  // Photos-field delta writes (view mode only): each landed upload / removed
  // tile commits a single-photo add/remove, so concurrent uploads can't
  // clobber each other.
  const handleAddPhoto = useCallback(
    (customFieldId: string, photoId: string) => {
      if (isCreateMode || !issue || !editableHere) return;
      return addPhotoFieldValue(issue.issueId, customFieldId, photoId);
    },
    [isCreateMode, issue, editableHere, addPhotoFieldValue],
  );
  const handleRemovePhoto = useCallback(
    (customFieldId: string, photoId: string) => {
      if (isCreateMode || !issue || !editableHere) return;
      return removePhotoFieldValue(issue.issueId, customFieldId, photoId);
    },
    [isCreateMode, issue, editableHere, removePhotoFieldValue],
  );

  // The description editor autosaves on a debounce and flushes on unmount, so
  // this fires from a background timer as often as from a keystroke — hence the
  // catch: an unhandled rejection on a field nobody is looking at is how a lost
  // write goes unnoticed.
  const handleUpdateDescription = useCallback(
    (description: string) => {
      if (isCreateMode) {
        createCtx.updateDraft({ description });
        return;
      }
      if (!issue || !editableHere) return;
      // Toast here, then rethrow: the editor's status line reads the
      // rejection as "Not saved", and it is the only caller. An error
      // payload (the union's non-Issue members) resolves without throwing,
      // so it is turned into a rejection first.
      const variables = { issueId: issue.issueId, description };
      return updateDescription({
        variables,
        // The editor flushes as the page hides or unloads; that write must
        // outlive the page.
        context: pageLeaveContext(variables),
      })
        .then((result) => {
          const payload = result.data?.updateIssue;
          if (payload?.__typename !== "Issue") {
            throw new Error(resultMessage(payload) ?? "Description not saved");
          }
        })
        .catch((err: unknown) => {
          toastMutationError(err, "Failed to update description");
          throw err;
        });
    },
    [isCreateMode, createCtx, issue, editableHere, updateDescription],
  );

  // ---------------------------------------------------------------------------
  // Resolve data sources for create vs view mode
  // ---------------------------------------------------------------------------

  const resolvedIssueTypes = isCreateMode ? createCtx.issueTypes : issueTypes;
  const resolvedDescription = isCreateMode
    ? createCtx.draft.description
    : (issue?.description ?? "");
  const resolvedIssueTypeId = isCreateMode ? createCtx.draft.issueTypeId : issue?.issueTypeId;
  // The hero's pick list: the workspace types, or — while they are still
  // loading — the issue's own type, so the control never reads empty.
  const heroIssueTypes = useMemo(
    () =>
      resolvedIssueTypes && resolvedIssueTypes.length > 0
        ? resolvedIssueTypes
        : issue?.issueType
          ? [issue.issueType]
          : [],
    [resolvedIssueTypes, issue?.issueType],
  );
  const resolvedValues = isCreateMode ? createCtx.draft.values : (values ?? undefined);
  const resolvedCustomFields = isCreateMode ? createCtx.customFieldDefs : customFields;
  // The project's type: its section layout, its required fields and the
  // fields it keeps on the summary while empty are read off it here, once.
  const summaryIssueType = useMemo(
    () =>
      resolvedIssueTypes?.find((it) => it.issueTypeId === resolvedIssueTypeId) ?? null,
    [resolvedIssueTypes, resolvedIssueTypeId],
  );
  const typeLayout = useMemo(
    () => resolveTypeSectionLayout(summaryIssueType),
    [summaryIssueType],
  );
  const resolvedRequiredFieldIds = useMemo(
    () =>
      isCreateMode
        ? createCtx.requiredFieldIds
        : (summaryIssueType?.fields ?? [])
            .filter((f) => f.required)
            .map((f) => f.customFieldId),
    [isCreateMode, createCtx, summaryIssueType],
  );
  const alwaysShownFieldIds = useMemo(
    () =>
      (summaryIssueType?.fields ?? [])
        .filter((f) => f.alwaysShown)
        .map((f) => f.customFieldId),
    [summaryIssueType],
  );

  // Server returns IssueType.fields in sortOrder ASC. This list drives the
  // custom-fields table row order so the order users set on the project
  // type edit screen is honored everywhere.
  const orderedTypeFieldIds = useMemo<string[]>(() => {
    if (isCreateMode) {
      return createCtx.issueTypeFields
        .filter((f) => f.field !== null)
        .map((f) => f.customFieldId);
    }
    if (!resolvedIssueTypeId || !issueTypes) return [];
    const type = issueTypes.find(
      (it) => it.issueTypeId === resolvedIssueTypeId,
    );
    return (type?.fields ?? [])
      .filter((f) => f.field !== null)
      .map((f) => f.customFieldId);
  }, [isCreateMode, createCtx, issueTypes, resolvedIssueTypeId]);

  // ---------------------------------------------------------------------------
  // Widget stack. The detail view hands the whole roster to the shared
  // `SummaryWidgetStack` with the type's order and visibility. Create mode
  // renders the subset a draft can hold, in the same order, and follows the
  // type's Description switch too.
  // ---------------------------------------------------------------------------

  const createModeWidgets = useMemo(
    () => [
      ISSUE_PROPERTIES_WIDGET,
      ...typeLayout.orderedWidgets.filter(
        (widget) =>
          CREATE_MODE_WIDGET_TYPES.includes(widget.type) && !typeLayout.hidden[widget.type],
      ),
    ],
    [typeLayout],
  );

  // Stack cards keep a left gutter for the hover drag grip (and the header
  // capsule's wash bleed); the page header sections take the same inset so
  // everything shares one content edge.
  const stackGutter = "lg" as const;
  // The outer `md` wrap holds the centered column off the dialog's edge on
  // desktop. The phone sheet has no such edge: its header already sits on
  // the 24px band, so the stack gutter alone is the sheet's content edge
  // (Spencer, 2026-09-30 — the gutter stays: it seats the pinned touch grip,
  // the capsule's 8px press-wash bleed and the inline-edit focus rings).
  const outerInset = isMobile ? undefined : "md";

  return (
    // The empty-row fold is per open: a new project starts folded (a create
    // draft never folds and takes the same key).
    <SummaryEmptyFoldProvider key={issue?.issueId ?? "create"}>
      <Box paddingBottom={SUMMARY_SCROLL_END_GUTTER} paddingX={outerInset} align="center">
        <Box fill="width" maxWidth={contentMaxWidth}>
          {(
            // The identity block owns the name — and, in the "hero" placement
            // (`typePlacement`), the Type as one outline control above it;
            // otherwise Type is a property row. `gap="md"` is the 16px from
            // the type control to the title box: the title's hover wash bleeds
            // 9px upward, so the wash clears the button by 7px and the text
            // sits ~25px under it — the ClickUp hero's own distance between
            // its label button and the task name.
            <Box
              gap="md"
              // The phone's tab hairline already splits chrome from content,
              // so the title needs less air under it than the desktop column.
              paddingTop={isMobile ? "lg" : "xl"}
              paddingBottom="md"
              paddingLeft={stackGutter}
              paddingRight={stackGutter}
            >
              {typeInHero ? (
                <Box direction="row" align="center">
                  <IssueTypeSelector
                    buttonVariant="outline"
                    issueTypes={heroIssueTypes}
                    onValueChange={handleIssueTypeChange}
                    readOnly={!editableHere}
                    dense
                    shape="square"
                    size="sm"
                    value={resolvedIssueTypeId}
                    valueMuted
                  />
                </Box>
              ) : null}
              {/* Title — Input when editable, static Text when read-only.
                  Tier:
                    view / comment → no input, just the name text.
                    edit / full_edit → editable Input, renames on blur. */}
              <Box id={isCreateMode ? "create-issue-name" : undefined}>
                {editableHere ? (
                  <Input
                    ref={nameInputRef}
                    key={isCreateMode ? "create" : issue?.issueId}
                    defaultValue={
                      isCreateMode
                        ? (createCtx?.draft.name ?? "")
                        : (issue?.name ?? "")
                    }
                    onValueChange={handleNameChange}
                    onBlur={handleNameBlur}
                    onKeyDown={handleNameKeyDown}
                    aria-label="Title"
                    // The missing-fields bar counts the title, so a failed
                    // submit must mark it too, or the count names nothing.
                    aria-invalid={titleMissing || undefined}
                    placeholder="Project title..."
                    unstyled
                    className={TITLE_INPUT}
                  />
                ) : (
                  <Text size="xl" weight="bold" truncate={2}>
                    {issue?.name ?? ""}
                  </Text>
                )}
              </Box>
            </Box>
          )}

          <Box>
            <IssueWidgetProvider
              value={{
                issue,
                stage,
                workflowId,
                address,
                assignees,
                issueTypes: resolvedIssueTypes,
                onIssueTypeChange: handleIssueTypeChange,
                values: resolvedValues,
                customFields: resolvedCustomFields,
                orderedTypeFieldIds,
                requiredFieldIds: resolvedRequiredFieldIds,
                alwaysShownFieldIds,
                onUpdateValue: handleCustomFieldUpdate,
                onAddPhoto:
                  !isCreateMode && editableHere ? handleAddPhoto : undefined,
                onRemovePhoto:
                  !isCreateMode && editableHere
                    ? handleRemovePhoto
                    : undefined,
                description: resolvedDescription,
                onUpdateDescription: editableHere
                  ? handleUpdateDescription
                  : undefined,
                onCloseIssue,
                focusCustomFieldId,
                isCreateMode,
                onNavigateTab,
                activity,
                activityLoading,
                notes,
                mediaItems,
              }}
            >
              {isCreateMode ? (
                // The detail stack's own rhythm (SummaryWidgetStack `xs`):
                // sections at the property rows' pitch.
                <Box
                  gap="xs"
                  paddingLeft={stackGutter}
                  paddingRight={stackGutter}
                >
                  {createModeWidgets.map((instance, index) => (
                    <Fragment key={`${instance.id}-${index}`}>
                      {/* The properties block leads; the sections after it
                          start a new list, so the same seam the detail stack
                          draws sits between them. */}
                      {index === 1 ? <SummarySectionDivider /> : null}
                      <WidgetCard
                        instance={instance}
                        canManage={false}
                        presentation="stack"
                        surface="canvas"
                        collapsible={instance.collapsible}
                        hideHeader={instance.hideHeader}
                        collapsed={collapsedSet.has(instance.type)}
                        onCollapsedChange={(collapsed) =>
                          setCollapsed(instance.type, collapsed)
                        }
                      />
                    </Fragment>
                  ))}
                </Box>
              ) : (
                <SummaryWidgetStack
                  fixedWidget={ISSUE_PROPERTIES_WIDGET}
                  sortableWidgets={ISSUE_SORTABLE_WIDGETS}
                  sectionOrder={summaryIssueType?.sectionOrder}
                  hidden={typeLayout.hidden}
                />
              )}
            </IssueWidgetProvider>
          </Box>
        </Box>
      </Box>

      {!isCreateMode && issue?.issueTypeId && resolvedIssueTypes && migrationTarget && (
        <MigrateIssueTypeDialog
          open
          onOpenChange={(o) => {
            if (!o) setMigrationTarget(null);
          }}
          issueTypes={resolvedIssueTypes}
          issueTypeId={migrationTarget}
          values={resolvedValues ?? {}}
          issueName={issue.name}
          onConfirm={handleMigrateConfirm}
        />
      )}
    </SummaryEmptyFoldProvider>
  );
}
